import React, { ReactNode, useEffect } from "react";
import clsx from "clsx";
import { X } from "lucide-react";

// Base Modal Component
interface ModalProps {
  children: ReactNode;
  isOpen: boolean;
  onClose: () => void;
  title?: string;
  size?: "sm" | "md" | "lg" | "xl" | "fullscreen";
  dismissible?: boolean;
  showCloseButton?: boolean;
  className?: string;
}

export const Modal: React.FC<ModalProps> = ({
  children,
  isOpen,
  onClose,
  title,
  size = "md",
  dismissible = true,
  showCloseButton = true,
  className,
}) => {
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
    }

    return () => {
      document.body.style.overflow = "unset";
    };
  }, [isOpen]);

  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape" && dismissible) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener("keydown", handleEscape);
    }

    return () => {
      document.removeEventListener("keydown", handleEscape);
    };
  }, [isOpen, dismissible, onClose]);

  if (!isOpen) return null;

  const sizeStyles = {
    sm: "max-w-sm",
    md: "max-w-lg",
    lg: "max-w-2xl",
    xl: "max-w-4xl",
    fullscreen: "w-screen h-screen max-w-none",
  };

  const handleOverlayClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget && dismissible) {
      onClose();
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={handleOverlayClick} />

      {/* Modal */}
      <div
        className={clsx(
          "relative w-full bg-slate-800 border border-slate-700 rounded-lg shadow-tracq-card text-white flex flex-col",
          sizeStyles[size],
          size === "fullscreen" ? "rounded-none h-screen" : "max-h-[90vh]",
          className,
        )}
      >
        {/* Header */}
        {title && (
          <div className="flex items-center justify-between p-6 border-b border-slate-700 bg-slate-800/50 flex-shrink-0">
            <h2 className="text-lg font-semibold text-white">{title}</h2>
            {showCloseButton && (
              <button
                onClick={onClose}
                className="p-1 hover:bg-slate-700 rounded-lg transition-colors text-slate-400 hover:text-white"
                disabled={!dismissible}
              >
                <X className="w-5 h-5" />
              </button>
            )}
          </div>
        )}

        {/* Content */}
        <div
          className={clsx(
            "bg-slate-800 flex-1 min-h-0",
            size === "fullscreen" ? "h-full overflow-y-auto" : "overflow-y-auto",
            !title && "p-6", // Only add padding if no title (for simple modals)
          )}
        >
          {children}
        </div>
      </div>
    </div>
  );
};

// Modal Header Component
interface ModalHeaderProps {
  children: ReactNode;
  className?: string;
}

export const ModalHeader: React.FC<ModalHeaderProps> = ({ children, className }) => {
  return (
    <div
      className={clsx(
        "flex items-center justify-between p-6 border-b border-slate-700 bg-slate-800/50 flex-shrink-0",
        className,
      )}
    >
      {children}
    </div>
  );
};

// Modal Body Component
interface ModalBodyProps {
  children: ReactNode;
  className?: string;
}

export const ModalBody: React.FC<ModalBodyProps> = ({ children, className }) => {
  return <div className={clsx("p-6", className)}>{children}</div>;
};

// Modal Footer Component
interface ModalFooterProps {
  children: ReactNode;
  className?: string;
}

export const ModalFooter: React.FC<ModalFooterProps> = ({ children, className }) => {
  return (
    <div
      className={clsx(
        "flex items-center justify-end gap-3 p-6 border-t border-slate-700 bg-slate-800/50 flex-shrink-0",
        className,
      )}
    >
      {children}
    </div>
  );
};

// Confirmation Dialog Component
interface ConfirmDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  title: string;
  message: string;
  confirmText?: string;
  cancelText?: string;
  variant?: "default" | "destructive";
  icon?: ReactNode;
  dismissible?: boolean;
}

export const ConfirmDialog: React.FC<ConfirmDialogProps> = ({
  isOpen,
  onClose,
  onConfirm,
  title,
  message,
  confirmText = "Confirm",
  cancelText = "Cancel",
  variant = "default",
  icon,
  dismissible = true,
}) => {
  const handleConfirm = () => {
    onConfirm();
    onClose();
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={title} size="sm" dismissible={dismissible}>
      <div className="space-y-4">
        {icon && <div className="flex justify-center">{icon}</div>}
        <p className="text-neutral-300 text-center">{message}</p>
        <div className="flex justify-end gap-3">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-neutral-400 hover:text-white transition-colors"
            disabled={!dismissible}
          >
            {cancelText}
          </button>
          <button
            onClick={handleConfirm}
            className={clsx(
              "px-4 py-2 text-sm font-medium rounded-md transition-colors",
              variant === "destructive"
                ? "bg-red-600 text-white hover:bg-red-700"
                : "bg-tracq-deep-blue text-white hover:bg-tracq-bright-blue",
            )}
          >
            {confirmText}
          </button>
        </div>
      </div>
    </Modal>
  );
};

// Drawer/Sheet Component
interface DrawerProps {
  children: ReactNode;
  isOpen: boolean;
  onClose: () => void;
  title?: string;
  position?: "left" | "right" | "top" | "bottom";
  size?: "sm" | "md" | "lg";
  dismissible?: boolean;
  className?: string;
}

export const Drawer: React.FC<DrawerProps> = ({
  children,
  isOpen,
  onClose,
  title,
  position = "right",
  size = "md",
  dismissible = true,
  className,
}) => {
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
    }

    return () => {
      document.body.style.overflow = "unset";
    };
  }, [isOpen]);

  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape" && dismissible) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener("keydown", handleEscape);
    }

    return () => {
      document.removeEventListener("keydown", handleEscape);
    };
  }, [isOpen, dismissible, onClose]);

  if (!isOpen) return null;

  const positionStyles = {
    left: "left-0 top-0 h-full",
    right: "right-0 top-0 h-full",
    top: "top-0 left-0 w-full",
    bottom: "bottom-0 left-0 w-full",
  };

  const sizeStyles = {
    sm: position === "left" || position === "right" ? "w-80" : "h-80",
    md: position === "left" || position === "right" ? "w-96" : "h-96",
    lg: position === "left" || position === "right" ? "w-[28rem]" : "h-[28rem]",
  };

  const handleOverlayClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget && dismissible) {
      onClose();
    }
  };

  return (
    <div className="fixed inset-0 z-50">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={handleOverlayClick} />

      {/* Drawer */}
      <div
        className={clsx(
          "absolute border border-neutral-700 shadow-xl",
          positionStyles[position],
          sizeStyles[size],
          position === "left" ? "border-r" : "",
          position === "right" ? "border-l" : "",
          position === "top" ? "border-b" : "",
          position === "bottom" ? "border-t" : "",
          className,
        )}
      >
        {/* Header */}
        {title && (
          <div className="flex items-center justify-between p-4 border-b border-neutral-700">
            <h2 className="text-lg font-semibold text-white">{title}</h2>
            <button
              onClick={onClose}
              className="p-1 hover:bg-neutral-800 rounded transition-colors"
              disabled={!dismissible}
            >
              <X className="w-5 h-5 text-neutral-400" />
            </button>
          </div>
        )}

        {/* Content */}
        <div className="p-4 h-full overflow-y-auto">{children}</div>
      </div>
    </div>
  );
};

// Alert Dialog Component
interface AlertDialogProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  message: string;
  variant?: "info" | "success" | "warning" | "error";
  buttonText?: string;
  dismissible?: boolean;
}

export const AlertDialog: React.FC<AlertDialogProps> = ({
  isOpen,
  onClose,
  title,
  message,
  variant = "info",
  buttonText = "OK",
  dismissible = true,
}) => {
  const variantStyles = {
    info: "text-blue-400",
    success: "text-green-400",
    warning: "text-yellow-400",
    error: "text-red-400",
  };

  const iconMap = {
    info: "ℹ️",
    success: "✅",
    warning: "⚠️",
    error: "❌",
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={title} size="sm" dismissible={dismissible}>
      <div className="space-y-4">
        <div className="flex items-center gap-3">
          <span className="text-2xl">{iconMap[variant]}</span>
          <p className={clsx("text-sm", variantStyles[variant])}>{message}</p>
        </div>
        <div className="flex justify-end">
          <button
            onClick={onClose}
            className={clsx(
              "px-4 py-2 text-sm font-medium rounded-md transition-colors",
              variant === "error"
                ? "bg-red-600 text-white hover:bg-red-700"
                : "bg-tracq-deep-blue text-white hover:bg-tracq-bright-blue",
            )}
          >
            {buttonText}
          </button>
        </div>
      </div>
    </Modal>
  );
};
